/**
 * Genesis Event Controller
 *
 * Orchestrator for the Genesis Event celebration.
 * Manages phase progression, audio, and completion tracking.
 *
 * Phases:
 * 1. Celebration - Confetti burst and achievement card
 * 2. Integration - Settling and fade out
 */

import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { Box, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Close } from '@mui/icons-material';
import PhaseCelebration from './phases/PhaseCelebration';
import PhaseIntegration from './phases/PhaseIntegration';
import { genesisAudio } from './audio/AudioController';
import { useGenesisEvent } from './useGenesisEvent';
import { GenesisPhase } from './types';
import { errorReporter } from '@/services/errorReportingService';
import './styles/genesis-event.css';

interface GenesisEventControllerProps {
  /** ID of the note being viewed */
  noteId?: string;
  /** Creator user ID of the note */
  noteCreatorUserId?: string;
  /** Callback when celebration completes */
  onComplete?: () => void;
  /** Callback when user skips */
  onSkip?: () => void;
}

const GenesisEventController = ({ noteId, noteCreatorUserId, onComplete, onSkip }: GenesisEventControllerProps) => {
  const { eligibility, startEvent, skipEvent, completeEvent } = useGenesisEvent(noteId, noteCreatorUserId);

  // Internal phase tracking for celebration phases
  const [celebrationPhase, setCelebrationPhase] = useState<GenesisPhase>(GenesisPhase.IDLE);
  const eventStartedRef = useRef(false);

  // Auto-start when eligible
  useEffect(() => {
    if (eligibility.eligible && !eventStartedRef.current && celebrationPhase === GenesisPhase.IDLE) {
      eventStartedRef.current = true;
      startEvent();
      setCelebrationPhase(GenesisPhase.CELEBRATION);
    }
  }, [eligibility.eligible, celebrationPhase, startEvent]);

  // Phase progression handlers
  const handleCelebrationComplete = useCallback(() => {
    setCelebrationPhase(GenesisPhase.INTEGRATION);
  }, []);

  const handleIntegrationComplete = useCallback(() => {
    setCelebrationPhase(GenesisPhase.COMPLETE);
    completeEvent()
      .then(() => {
        onComplete?.();
      })
      .catch((err) => {
        errorReporter.expected('[Genesis] Failed to mark event completed', err);
      });
  }, [completeEvent, onComplete]);

  // Skip handler
  const handleSkip = useCallback(() => {
    genesisAudio.stopAll();
    setCelebrationPhase(GenesisPhase.COMPLETE);
    skipEvent()
      .then(() => {
        onSkip?.();
      })
      .catch((err) => {
        errorReporter.expected('[Genesis] Failed to skip event', err);
      });
  }, [skipEvent, onSkip]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && celebrationPhase !== GenesisPhase.IDLE && celebrationPhase !== GenesisPhase.COMPLETE) {
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [celebrationPhase, handleSkip]);

  // Check reduced motion preference
  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  // Don't render if not eligible or completed
  if (!eligibility.eligible && celebrationPhase === GenesisPhase.IDLE) {
    return null;
  }

  if (celebrationPhase === GenesisPhase.COMPLETE) {
    return null;
  }

  // Reduced motion: just show the achievement card
  if (prefersReducedMotion && celebrationPhase !== GenesisPhase.IDLE) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0, 0, 0, 0.7)',
        }}
      >
        <IconButton
          onClick={handleSkip}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.3)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
          }}
        >
          <Close />
        </IconButton>
        <PhaseCelebration active={true} onComplete={handleIntegrationComplete} duration={4000} />
      </Box>
    );
  }

  return (
    <AnimatePresence>
      {celebrationPhase !== GenesisPhase.IDLE && (
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            pointerEvents: 'auto',
          }}
        >
          {/* Skip button */}
          <IconButton
            component={motion.button}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            whileHover={{ opacity: 1 }}
            onClick={handleSkip}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.3)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
              zIndex: 10003,
            }}
          >
            <Close />
          </IconButton>

          {/* Celebration - confetti + achievement card */}
          <PhaseCelebration
            active={celebrationPhase === GenesisPhase.CELEBRATION}
            onComplete={handleCelebrationComplete}
          />

          {/* Phase 4: Integration */}
          <PhaseIntegration
            active={celebrationPhase === GenesisPhase.INTEGRATION}
            onComplete={handleIntegrationComplete}
          />
        </Box>
      )}
    </AnimatePresence>
  );
};

export default GenesisEventController;
